import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Terminal, Code, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AuthForm from './auth/AuthForm';
import HeroInput from './HeroInput';

const Hero = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingDescription, setPendingDescription] = useState('');

  const handleSubmit = (description: string) => {
    if (!currentUser) {
      setPendingDescription(description);
      setShowAuthDialog(true);
      return;
    }

    sessionStorage.setItem('projectDescription', description);
    navigate('/results');
  };

  const handleAuthSuccess = () => {
    setShowAuthDialog(false);
    if (pendingDescription) {
      sessionStorage.setItem('projectDescription', pendingDescription);
      navigate('/results');
    }
  };

  return (
    <>
      <div id="prompt-generator" className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-purple-600/20 rounded-full filter blur-3xl opacity-50 animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-600/20 rounded-full filter blur-3xl opacity-40 animate-pulse-slow"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-fuchsia-600/5 rounded-full filter blur-3xl opacity-20 animate-pulse-slow"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight font-display">
                <span className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-500 bg-clip-text text-transparent">Idea to prompts,</span> <span className="text-white">instantly</span>
              </h1>
              <h2 className="text-xl md:text-2xl font-medium text-white/90 mb-6">
                Perfect AI prompts for your coding assistant
              </h2>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <p className="text-lg md:text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
                We don't write code, we engineer the perfect prompts for your AI tools. 
                Describe your app idea or where you're stuck, and we'll generate the ideal AI instructions.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <HeroInput onSubmit={handleSubmit} />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
            >
              <div className="elevated-card glass-hover">
                <div className="flex flex-col items-center text-center">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-600/20 to-indigo-600/20 flex items-center justify-center mb-4">
                    <Terminal className="h-6 w-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Stuck on Prompts?</h3>
                  <p className="text-muted-foreground">
                    When you're blocked on how to instruct AI to code your vision, we provide the exact prompts needed.
                  </p>
                </div>
              </div>
              
              <div className="elevated-card glass-hover">
                <div className="flex flex-col items-center text-center">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-600/20 to-violet-600/20 flex items-center justify-center mb-4">
                    <Code className="h-6 w-6 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">No Coding Required</h3>
                  <p className="text-muted-foreground">
                    We don't write code for you—we craft the perfect prompts for your AI tools to generate the best code.
                  </p>
                </div>
              </div>
              
              <div className="elevated-card glass-hover">
                <div className="flex flex-col items-center text-center">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-violet-600/20 to-fuchsia-600/20 flex items-center justify-center mb-4">
                    <Wand2 className="h-6 w-6 text-violet-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">AI-Optimized Results</h3>
                  <p className="text-muted-foreground">
                    Our specialized prompts are engineered for maximum clarity and specificity with AI coding assistants.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign in to continue</DialogTitle>
            <DialogDescription>
              Create an account or sign in to generate and save AI prompts
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <AuthForm onSuccess={handleAuthSuccess} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Hero;
